/*
 * speed-test/stats.js — the arithmetic of /speed-test, with no DOM and no network.
 *
 * Everything that turns raw byte counts and round-trip times into the numbers a
 * person reads lives here, so it can be tested in node and can never quietly
 * differ between the live readout, the saved history and the PDF report.
 *
 * ── Why throughput is a TRIMMED mean of time slices (measured 2026-09-13) ──
 * Support #"three other testers all show much more". On the same line and in
 * the same minute fast.com showed 89 Mbps and our production test 24.9. Two
 * causes, both measured, both fixed in the engine rather than hidden here:
 *   1. every "parallel stream" rode ONE HTTP/2 connection (NetLog: one session
 *      for all 56 requests), i.e. one congestion window — one connection read
 *      35–47 Mbps where six read 73–78 on the same server;
 *   2. the result averaged the whole post-ramp window, so every Wi-Fi dip was
 *      billed in full.
 * This module owns (2). Ookla publishes its method: the transfer is cut into
 * ~20 time slices, the slowest 30 % and fastest 10 % are discarded and the rest
 * averaged. That is what `summarizeThroughput` does, and the FAQ says so in
 * plain words — the rule is disclosed, not tuned.
 *
 * ── Why bufferbloat thresholds are Waveform's ──
 * They are the published reference for the metric (A+ <5 ms … F ≥400 ms of
 * added latency under load). The old engine graded 52 % of all RU runs F —
 * not because routers are that bad, but because its "loaded" pings travelled
 * over the SAME connection as the download and queued behind our own bytes
 * (same line: +80…+510 ms on the transfer connection, +6…+39 ms on a separate
 * one). The engine now probes on a connection that carries no transfer.
 */
(function (root) {
    'use strict';

    function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }
    function isNum(v) { return typeof v === 'number' && isFinite(v); }

    function median(arr) {
        var a = (arr || []).filter(isNum).slice().sort(function (x, y) { return x - y; });
        if (!a.length) return null;
        var m = a.length >> 1;
        return a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2;
    }

    function percentile(arr, q) {
        var a = (arr || []).filter(isNum).slice().sort(function (x, y) { return x - y; });
        if (!a.length) return null;
        var pos = clamp(q, 0, 1) * (a.length - 1);
        var lo = Math.floor(pos), hi = Math.ceil(pos);
        return a[lo] + (a[hi] - a[lo]) * (pos - lo);
    }

    /* Mean after discarding the lowest `lowFrac` and highest `highFrac` share.
       With fewer than 5 values there is nothing meaningful to trim. */
    function trimmedMean(values, lowFrac, highFrac) {
        var a = (values || []).filter(isNum).slice().sort(function (x, y) { return x - y; });
        if (!a.length) return null;
        if (a.length < 5) return a.reduce(function (s, v) { return s + v; }, 0) / a.length;
        var lo = Math.floor(a.length * (lowFrac == null ? 0.3 : lowFrac));
        var hi = a.length - Math.floor(a.length * (highFrac == null ? 0.1 : highFrac));
        var kept = a.slice(lo, Math.max(lo + 1, hi));
        return kept.reduce(function (s, v) { return s + v; }, 0) / kept.length;
    }

    /* Byte events {t: epochMs, n: bytes} from any number of lanes → a cumulative
       series sorted by time. Lanes post on their own clocks, but all of them are
       `performance.timeOrigin + performance.now()`, i.e. the same epoch. */
    function cumulative(events) {
        var ev = (events || []).filter(function (e) { return e && isNum(e.t) && isNum(e.n) && e.n > 0; })
            .slice().sort(function (a, b) { return a.t - b.t; });
        var out = [], sum = 0;
        for (var i = 0; i < ev.length; i++) { sum += ev[i].n; out.push({ t: ev[i].t, b: sum }); }
        return out;
    }

    /* Cumulative bytes at time t, linearly interpolated between reports. Before
       the first report the line had carried nothing we know of → 0. */
    function bytesAt(series, t) {
        if (!series.length || t <= series[0].t) {
            if (!series.length) return 0;
            return t < series[0].t ? 0 : series[0].b;
        }
        var last = series[series.length - 1];
        if (t >= last.t) return last.b;
        var lo = 0, hi = series.length - 1;
        while (hi - lo > 1) { var mid = (lo + hi) >> 1; if (series[mid].t <= t) lo = mid; else hi = mid; }
        var a = series[lo], b = series[hi];
        return a.b + (b.b - a.b) * ((t - a.t) / Math.max(1, b.t - a.t));
    }

    /* Mbps per slice between `from` and `to` (epoch ms). */
    function sliceRates(series, from, to, slices) {
        var out = [];
        if (!(to > from) || slices < 1) return out;
        var w = (to - from) / slices;
        for (var i = 0; i < slices; i++) {
            var s = from + i * w, e = s + w;
            out.push(((bytesAt(series, e) - bytesAt(series, s)) * 8) / (w / 1000) / 1e6);
        }
        return out;
    }

    /*
     * The headline number for one direction.
     *   events  — byte reports from every transfer lane
     *   start   — epoch ms when the phase began
     *   end     — epoch ms when counting stopped
     *   rampMs  — connection set-up + slow start, excluded from the slices
     * Returns null when too little was measured to state a speed honestly.
     */
    function summarizeThroughput(events, start, end, opts) {
        opts = opts || {};
        var rampMs = isNum(opts.rampMs) ? opts.rampMs : 2000;
        var slices = opts.slices || 20;
        var series = cumulative(events);
        if (series.length < 2) return null;
        var total = series[series.length - 1].b;
        var from = start + rampMs;
        // A line so slow that the ramp swallows the phase: fall back to the
        // whole window rather than inventing precision.
        if (end - from < 1500) from = start;
        var rates = sliceRates(series, from, end, slices);
        if (!rates.length) return null;
        var mbps = trimmedMean(rates, 0.3, 0.1);
        var mean = rates.reduce(function (s, v) { return s + v; }, 0) / rates.length;
        return {
            mbps: mbps,
            mean: mean,
            peak: Math.max.apply(null, rates),
            p10: percentile(rates, 0.1),
            p90: percentile(rates, 0.9),
            stability: stabilityOf(rates),
            slices: rates,
            bytes: total,
            seconds: (end - from) / 1000,
        };
    }

    /* 100 · (1 − coefficient of variation) of the slice rates, 0…100. */
    function stabilityOf(rates) {
        var a = (rates || []).filter(isNum);
        if (a.length < 4) return null;
        var mean = a.reduce(function (s, v) { return s + v; }, 0) / a.length;
        if (!(mean > 0)) return null;
        var sd = Math.sqrt(a.reduce(function (s, v) { return s + (v - mean) * (v - mean); }, 0) / a.length);
        return Math.round(clamp(100 * (1 - sd / mean), 0, 100));
    }

    /* Live readout: rate over the trailing window ending at `now`. */
    function recentRate(events, now, windowMs) {
        var w = windowMs || 1000, sum = 0, first = Infinity;
        for (var i = events.length - 1; i >= 0; i--) {
            var e = events[i];
            if (e.t <= now - w) break;
            sum += e.n; if (e.t < first) first = e.t;
        }
        if (!sum) return 0;
        return (sum * 8) / (w / 1000) / 1e6;
    }

    /*
     * Latency from sequential round trips (ms). The first `warm` samples carry
     * connection warm-up and are dropped. Jitter is the mean absolute
     * difference between consecutive samples — the definition Ookla and
     * Cloudflare both display.
     */
    function summarizeLatency(samples, warm) {
        var s = (samples || []).filter(isNum);
        var drop = warm == null ? 2 : warm;
        if (s.length > drop + 2) s = s.slice(drop);
        if (s.length < 3) return null;
        var jitterSum = 0;
        for (var i = 1; i < s.length; i++) jitterSum += Math.abs(s[i] - s[i - 1]);
        return {
            median: median(s),
            min: Math.min.apply(null, s),
            max: Math.max.apply(null, s),
            jitter: jitterSum / (s.length - 1),
            count: s.length,
        };
    }

    /* Waveform grading of the latency INCREASE under load. */
    function bufferbloatGrade(increaseMs) {
        if (!isNum(increaseMs)) return null;
        if (increaseMs < 5) return 'a+';
        if (increaseMs < 30) return 'a';
        if (increaseMs < 60) return 'b';
        if (increaseMs < 200) return 'c';
        if (increaseMs < 400) return 'd';
        return 'f';
    }

    function bufferbloat(idleMedian, loadedSamples) {
        var loaded = median(loadedSamples);
        if (!isNum(idleMedian) || !isNum(loaded) || (loadedSamples || []).length < 3) return null;
        var inc = Math.max(0, loaded - idleMedian);
        return { idle: idleMedian, loaded: loaded, increase: inc, grade: bufferbloatGrade(inc), count: loadedSamples.length };
    }

    /*
     * 0–100 composite. Weights are the ones the FAQ has published since v3:
     * download 30 %, upload 20 %, latency 20 %, jitter 10 %, bufferbloat 20 %.
     * A metric that could not be measured is left out and the rest renormalised.
     */
    function qualityScore(m) {
        var parts = [];
        if (isNum(m.download)) parts.push([30, clamp(Math.log10(Math.max(1, m.download)) / 3, 0, 1)]);
        if (isNum(m.upload)) parts.push([20, clamp(Math.log10(Math.max(1, m.upload)) / Math.log10(500), 0, 1)]);
        if (isNum(m.ping)) parts.push([20, clamp(1 - Math.max(0, m.ping - 5) / 145, 0, 1)]);
        if (isNum(m.jitter)) parts.push([10, clamp(1 - m.jitter / 50, 0, 1)]);
        if (isNum(m.bloat)) {
            var g = bufferbloatGrade(m.bloat);
            parts.push([20, { 'a+': 1, a: 1, b: 0.75, c: 0.45, d: 0.15, f: 0 }[g]]);
        }
        var wsum = parts.reduce(function (s, p) { return s + p[0]; }, 0);
        if (!wsum) return null;
        var score = parts.reduce(function (s, p) { return s + p[0] * p[1]; }, 0) * 100 / wsum;
        return Math.round(clamp(score, 0, 100));
    }

    function scoreBand(score) {
        if (!isNum(score)) return null;
        if (score >= 80) return 'excellent';
        if (score >= 60) return 'good';
        if (score >= 40) return 'fair';
        return 'poor';
    }

    /*
     * What the line is good for. Thresholds are the vendors' own published
     * minimums (Netflix 4K 15 Mbps → we ask 25 for headroom, Zoom HD group
     * 3.8/3.0, GeForce Now 1080p 25 / 45 ms), not taste.
     */
    var USES = [
        { key: 'streaming_4k', icon: '📺', need: { dl: 25 } },
        { key: 'streaming_hd', icon: '🎬', need: { dl: 5 } },
        { key: 'video_call', icon: '📹', need: { dl: 3.8, ul: 3, ping: 150, jitter: 40 } },
        { key: 'gaming', icon: '🎮', need: { dl: 5, ul: 1, ping: 60, jitter: 20, bloat: 60 } },
        { key: 'cloud_gaming', icon: '☁️', need: { dl: 25, ul: 5, ping: 45, jitter: 10, bloat: 30 } },
        { key: 'remote_work', icon: '💻', need: { dl: 10, ul: 5, ping: 100 } },
        { key: 'uploads', icon: '⬆️', need: { ul: 20 } },
    ];

    /* Worst headroom ratio over the thresholds a use-case names (≥1 = met). */
    function headroom(need, m) {
        var worst = Infinity, known = 0;
        function up(have, min) { if (!isNum(have)) return; known++; worst = Math.min(worst, have / min); }
        function down(have, max) { if (!isNum(have)) return; known++; worst = Math.min(worst, max / Math.max(0.5, have)); }
        if (need.dl) up(m.download, need.dl);
        if (need.ul) up(m.upload, need.ul);
        if (need.ping) down(m.ping, need.ping);
        if (need.jitter) down(m.jitter, need.jitter);
        if (need.bloat) down(m.bloat, need.bloat);
        return known ? worst : null;
    }

    /*
     * How many at the same time — the question behind "is 50 Mbps enough for
     * a family". Only for the uses that are a STREAM of a known bitrate
     * (video, a call); a game or a backup is not counted in copies. Latency
     * needs are a yes/no gate, not a divisor: two calls do not halve the ping.
     */
    function concurrent(key, m) {
        var u = null;
        for (var i = 0; i < USES.length; i++) if (USES[i].key === key) u = USES[i];
        if (!u || !(key === 'streaming_4k' || key === 'streaming_hd' || key === 'video_call')) return null;
        var n = Infinity;
        if (u.need.dl) { if (!isNum(m.download)) return null; n = Math.min(n, m.download / u.need.dl); }
        if (u.need.ul) { if (!isNum(m.upload)) return null; n = Math.min(n, m.upload / u.need.ul); }
        if (u.need.ping && isNum(m.ping) && m.ping > u.need.ping) return 0;
        if (u.need.jitter && isNum(m.jitter) && m.jitter > u.need.jitter) return 0;
        return isFinite(n) ? Math.min(99, Math.floor(n)) : null;
    }

    function useCases(m) {
        return USES.map(function (u) {
            var h = headroom(u.need, m);
            var level = h == null ? 'unknown' : h >= 2 ? 'excellent' : h >= 1 ? 'good' : h >= 0.5 ? 'marginal' : 'bad';
            return { key: u.key, icon: u.icon, level: level, headroom: h, count: concurrent(u.key, m) };
        });
    }

    /*
     * When in the day the line is slow. Evening congestion ("by 9 pm nothing
     * loads") is the most common complaint about a home line, and a single test
     * cannot show it. Median download per local hour; an hour with fewer than
     * `minPerHour` tests is reported but not ranked.
     */
    function hourProfile(rows, minPerHour) {
        var min = minPerHour || 1;
        var buckets = [];
        for (var h = 0; h < 24; h++) buckets.push([]);
        (rows || []).forEach(function (r) {
            if (!r || !isNum(r.download) || !isNum(r.timestamp)) return;
            buckets[new Date(r.timestamp).getHours()].push(r.download);
        });
        var hours = buckets.map(function (b, i) { return { h: i, n: b.length, median: median(b) }; });
        var ranked = hours.filter(function (x) { return x.n >= min && isNum(x.median); });
        var best = null, worst = null;
        ranked.forEach(function (x) {
            if (!best || x.median > best.median) best = x;
            if (!worst || x.median < worst.median) worst = x;
        });
        return { hours: hours, filled: ranked.length, best: best, worst: worst };
    }

    /*
     * Where in the home the line is slow. A test taken next to the router and
     * one taken in the bedroom on the same line answer "is it the provider or
     * the Wi-Fi" — the question the plan comparison alone cannot. Places are
     * the person's own labels, compared case-insensitively.
     */
    function byPlace(rows) {
        var map = {};
        (rows || []).forEach(function (r) {
            if (!r || !isNum(r.download)) return;
            var name = String(r.place || '').trim();
            if (!name) return;
            var k = name.toLowerCase();
            if (!map[k]) map[k] = { place: name, dl: [], ul: [], ping: [], last: 0 };
            map[k].dl.push(r.download);
            if (isNum(r.upload)) map[k].ul.push(r.upload);
            if (isNum(r.ping)) map[k].ping.push(r.ping);
            if ((r.timestamp || 0) >= map[k].last) { map[k].last = r.timestamp || 0; map[k].place = name; }
        });
        return Object.keys(map).map(function (k) {
            var p = map[k];
            return { place: p.place, n: p.dl.length, download: median(p.dl), upload: median(p.ul), ping: median(p.ping), last: p.last };
        }).sort(function (a, b) { return b.download - a.download; });
    }

    /* This place against the best other place: a ratio only when it matters (≥ 1.6×). */
    function placeGap(rows, place) {
        var name = String(place || '').trim().toLowerCase();
        if (!name) return null;
        var list = byPlace(rows);
        if (list.length < 2) return null;
        var here = null, best = null;
        list.forEach(function (p) {
            if (p.place.toLowerCase() === name) here = p;
            else if (!best || p.download > best.download) best = p;
        });
        if (!here || !best || !(here.download > 0)) return null;
        var ratio = best.download / here.download;
        return ratio >= 1.6 ? { place: best.place, ratio: ratio, here: here.download, there: best.download } : null;
    }

    /* Measured download as a share of the plan the person typed in. */
    function planShare(measured, plan) {
        if (!isNum(measured) || !isNum(plan) || plan <= 0) return null;
        var pct = measured / plan * 100;
        return { pct: pct, band: pct >= 90 ? 'full' : pct >= 70 ? 'most' : pct >= 40 ? 'half' : 'low' };
    }

    /*
     * Short, actionable reasons — only ones the measurements actually support.
     * Codes, not text: the page translates them.
     */
    function diagnose(m) {
        var out = [];
        var share = planShare(m.download, m.plan);
        if (share && share.pct < 70) {
            out.push(m.netType === 'wifi' || m.netType == null ? 'plan_low_wifi' : 'plan_low');
        }
        if (isNum(m.bloat) && m.bloat >= 60) out.push('bufferbloat');
        if (isNum(m.jitter) && m.jitter >= 30) out.push('jitter');
        if (isNum(m.ping) && m.ping >= 120) out.push('far');
        if (isNum(m.stability) && m.stability < 50) out.push('unstable');
        if (m.cpuBusy) out.push('cpu_busy');
        if (m.singleMode && share && share.pct < 90) out.push('try_multi');
        if (isNum(m.download) && isNum(m.upload) && m.upload > 0 && m.download / m.upload > 15 && m.upload < 10) out.push('asymmetric');
        return out.slice(0, 3);
    }

    /* Gauge scale: Ookla-style non-linear ticks, ×10 per range. */
    var SCALES = [
        [0, 1, 5, 10, 20, 30, 50, 75, 100],
        [0, 10, 50, 100, 200, 300, 500, 750, 1000],
        [0, 100, 500, 1000, 2000, 3000, 5000, 7500, 10000],
    ];

    function scaleFor(mbps) {
        for (var i = 0; i < SCALES.length; i++) {
            if (!(mbps > SCALES[i][SCALES[i].length - 1] * 0.92)) return SCALES[i];
        }
        return SCALES[SCALES.length - 1];
    }

    /* Value → 0…1 along the arc, piecewise-linear between ticks. */
    function gaugeFraction(mbps, ticks) {
        if (!(mbps > 0)) return 0;
        var n = ticks.length - 1;
        if (mbps >= ticks[n]) return 1;
        for (var i = 1; i <= n; i++) {
            if (mbps <= ticks[i]) {
                return (i - 1 + (mbps - ticks[i - 1]) / (ticks[i] - ticks[i - 1])) / n;
            }
        }
        return 1;
    }

    /* History summary for the report and the monitoring panel. */
    function summarizeHistory(rows, plan) {
        var r = (rows || []).filter(function (x) { return x && isNum(x.download); });
        if (!r.length) return null;
        var dl = r.map(function (x) { return x.download; });
        var ul = r.map(function (x) { return x.upload; }).filter(isNum);
        var ping = r.map(function (x) { return x.ping; }).filter(isNum);
        var below = null;
        if (isNum(plan) && plan > 0) {
            below = r.filter(function (x) { return x.download < plan * 0.7; }).length;
        }
        return {
            count: r.length,
            from: Math.min.apply(null, r.map(function (x) { return x.timestamp || 0; })),
            to: Math.max.apply(null, r.map(function (x) { return x.timestamp || 0; })),
            dlMedian: median(dl), dlMin: Math.min.apply(null, dl), dlMax: Math.max.apply(null, dl),
            ulMedian: median(ul), pingMedian: median(ping),
            belowPlan: below,
            belowPlanPct: below == null ? null : below / r.length * 100,
        };
    }

    var API = {
        median: median, percentile: percentile, trimmedMean: trimmedMean,
        cumulative: cumulative, bytesAt: bytesAt, sliceRates: sliceRates,
        summarizeThroughput: summarizeThroughput, stabilityOf: stabilityOf, recentRate: recentRate,
        summarizeLatency: summarizeLatency, bufferbloatGrade: bufferbloatGrade, bufferbloat: bufferbloat,
        qualityScore: qualityScore, scoreBand: scoreBand, useCases: useCases, USES: USES, concurrent: concurrent,
        planShare: planShare, diagnose: diagnose, scaleFor: scaleFor, gaugeFraction: gaugeFraction,
        summarizeHistory: summarizeHistory, SCALES: SCALES,
        hourProfile: hourProfile, byPlace: byPlace, placeGap: placeGap,
    };
    if (typeof module !== 'undefined' && module.exports) module.exports = API;
    root.SpeedTestStats = API;
})(typeof self !== 'undefined' ? self : this);
